import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes, { number } from 'prop-types';
import { enqueueSnackbar } from 'actions';
import clsx from 'clsx';
import _ from 'underscore';
import validate from 'validate.js';
import uuid from 'uuid/v1';
import moment from 'moment';
import { makeStyles } from '@mui/styles';
import {
	Card,
	List,
	ListItem,
	ListItemIcon,
	ListItemText,
	Typography,
	Tooltip,
	Divider,
	CardActions,
	CardContent,
	colors,
	Grid,
	Button,
	TextField
} from '@mui/material';
import DatePicker from 'components/DatePicker/DatePicker.js';
import { SelectField, Checkbox } from 'components';
import { SetFormState, GetClusterDetails, GenerateReport } from '../../actions/ReportsKpisActions';

const useStyles = makeStyles((theme) => ({
	root: {
		marginTop: theme.spacing(2),
	},
	value: {
		fontWeight: theme.typography.fontWeightMedium,
		align: 'center'
	},
	type: {
		fontWeight: theme.typography.fontWeightMedium,
	},
	warning: {
		color: colors.orange[500]
	},
	generate: {
		color: theme.palette.white,
		backgroundColor: colors.green[600],
		'&:hover': {
			backgroundColor: colors.green[900],
		},
	},
}));

const ClusterInfo = (props) => {
	const { className, ...rest } = props;
	const dispatch = useDispatch();
	const classes = useStyles();
	const setFormState = formState => dispatch(SetFormState(formState));
	const formState = useSelector(state => state.reportsKpisReducer.formState);
	const userId = useSelector((state) => state.session.user.id);
	const clusterDetails = useSelector((state) => state.reportsKpisReducer.clusterDetails);
	const clustersNames = useSelector((state) => state.reportsKpisReducer.clustersNames);
	const superClusters = useSelector((state) => state.reportsKpisReducer.superClusters);

	const _generate = () => {
		dispatch(GenerateReport(
			userId, formState.values.searchType, formState.values.clusterName, formState.values.host, formState.values.type,
			formState.values.numberOfDays, formState.values.mocnActivationDateA, clusterDetails.benefit, formState.values.exportCellsDetails
		));
		dispatch(enqueueSnackbar({ message: 'Report Sent to Queue', options: { variant: 'info' } }));
	};

	useMemo(() => {

		const data1 = new Date(clusterDetails.mocnActivationDateA);
		const data2 = moment.utc();

		// Diferença em milissegundos
		const diffMs = data2 - data1;

		// Converter para dias (1 dia = 1000 * 60 * 60 * 24 ms)
		const diffDias = Math.floor(diffMs / (1000 * 60 * 60 * 24));

		setFormState({
			...formState,
			values: {
				...formState.values,
				'mocnActivationDateA': clusterDetails.mocnActivationDateA,
				'numberOfDays': Math.abs(diffDias)
			},
			touched: {
				...formState.touched,
				'mocnActivationDateA': true
			}
		});
	}, [clusterDetails]);

	const _handleDateChange = (value, name, type = 'date') => {
		let formattedValue = null;

		if (value) {
			if (type === 'date') {
				formattedValue = value.startOf('day').format('YYYY-MM-DD');
			} else {
				formattedValue = value.format('YYYY-MM-DD HH:mm:ss');
			}
		}

		setFormState({
			...formState,
			values: {
				...formState.values,
				[name]: formattedValue
			},
			touched: {
				...formState.touched,
				[name]: true
			}
		});
	};

	const [errorState, setErrorState] = useState(false);
	const [errorMessage, setErrorMessage] = useState('');

	const _handleChange = event => {

		setErrorState(false);
		setErrorMessage('');

		let updatedValues = {
			...formState.values,
			[event.target.name]:
				event.target.type === 'checkbox'
					? event.target.checked
					: event.target.value
		};

		// Handle numberOfDays based on type
		if (event.target.name === 'type') {
			if (event.target.value === 'd0') {
				updatedValues.numberOfDays = 0;
			} else if (event.target.value === 'd+1') {
				updatedValues.numberOfDays = 1;
			}
			// For 'd+n' keep current value or reset to empty
			else if (event.target.value === 'd+n') {
				updatedValues.numberOfDays = '';
			}
		}

		if (event.target.name === 'numberOfDays' && formState.values.type === 'd+n') {
			const isValidInteger = event.target.value === '' || /^-?\d+$/.test(event.target.value);

			if (!isValidInteger) {
				setErrorState(true);
				setErrorMessage('Invalid Number');
			}
		}

		if (event.target.name === 'clusterName') {
			dispatch(GetClusterDetails(userId, event.target.value, formState.values.host));
		}

		if (event.target.name === 'superCluster') {
			const selectedSuperCluster = superClusters.find(sc => sc.value === event.target.value);
			updatedValues.clusterName = event.target.value;
			updatedValues.mocnActivationDateA = selectedSuperCluster ? selectedSuperCluster.extra : null;
		}

		setFormState({
			...formState,
			values: updatedValues,
			touched: {
				...formState.touched,
				[event.target.name]: true
			}
		});
	};

	const types = [
		{
			value: 'd0',
			text: 'd0',
		},
		{
			value: 'd+1',
			text: 'd+1',
		},
		{
			value: 'd+7',
			text: 'd+7',
		},
		{
			value: 'd+n',
			text: 'd+n',
		},
		{
			value: 'd today',
			text: 'd today',
		}
	];

	return (
		<Card {...rest} className={clsx(classes.root, className)}>
			<List>
				<ListItem
					key={1}
				>
					<Grid container spacing={1}>
						<Grid item md={3} xs={3} hidden={formState.values.searchType !== "ClusterName"}>
							<SelectField
								id="clusterName"
								name="clusterName"
								hintText="Cluster Name"
								defaultValue=""
								menuItems={(clustersNames) || []}
								value={formState.values.clusterName}
								onChange={_handleChange}
								sx={{
									'& .MuiInputBase-input': {
										backgroundColor: 'white',
										'&.Mui-disabled': {
											color: 'dimGray',
											WebkitTextFillColor: 'dimGray'
										}
									},
								}}
								disabled={formState.values.host === undefined}
							/>
						</Grid>
						<Grid item md={3} xs={3} hidden={formState.values.searchType !== "SuperCluster"}>
							<SelectField
								id="superCluster"
								name="superCluster"
								hintText="Super Cluster"
								defaultValue=""
								menuItems={(superClusters) || []}
								value={formState.values.superCluster}
								onChange={_handleChange}
								sx={{
									'& .MuiInputBase-input': {
										backgroundColor: 'white',
										'&.Mui-disabled': {
											color: 'dimGray',
											WebkitTextFillColor: 'dimGray'
										}
									},
								}}
							/>
						</Grid>
						<Grid item md={1} xs={1}>
							<SelectField
								id="type"
								name="type"
								hintText="Type"
								defaultValue=""
								menuItems={types}
								value={formState.values.type}
								onChange={_handleChange}
								sx={{
									'& .MuiInputBase-input': {
										backgroundColor: 'white',
										'&.Mui-disabled': {
											color: 'dimGray',
											WebkitTextFillColor: 'dimGray'
										}
									},
								}}
							/>
						</Grid>
						<Grid item md={2} xs={2} hidden={formState.values.type !== 'd+n'}>
							<TextField
								id="numberOfDays"
								name="numberOfDays"
								label="Number Of Days"
								variant="outlined"
								error={errorState}
								fullWidth
								helperText={errorMessage}
								value={formState.values.numberOfDays || ''}
								onChange={_handleChange}
								sx={{
									'& .MuiInputBase-input': {
										backgroundColor: 'white',
										'&.Mui-disabled': {
											color: 'dimGray',
											WebkitTextFillColor: 'dimGray'
										}
									},
								}}
							/>
						</Grid>
						<Grid item md={2} xs={2}>
							<DatePicker
								id="mocnActivationDateA"
								name="mocnActivationDateA"
								label="MOCN Activation Date (A)"
								value={formState.values.mocnActivationDateA}
								onChange={value => _handleDateChange(value, 'mocnActivationDateA', 'date')}
								format="DD/MM/YYYY"
							/>
						</Grid>
					</Grid>
				</ListItem>
			</List>
			<Divider />
			<CardActions>
				<Button
					className={classes.generate}
					onClick={_generate}
					variant="contained"
					disabled={
						formState.values.mocnActivationDateA === 'Invalid date'
						|| formState.values.mocnActivationDateA === undefined
						|| errorState
						|| formState.values.type === undefined
						|| (formState.values.type === 'd+n' && (formState.values.numberOfDays === '' || formState.values.numberOfDays === undefined))
					}
				>
					Generate
				</Button>
			</CardActions>
		</Card >
	);
};

ClusterInfo.propTypes = {
	className: PropTypes.string
};

export default ClusterInfo;
